import { useState } from 'react';
import { CalendarDaysIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { SolidStatusPill, formatEventWindow } from '@odo/shared-ui';
import { formatDateTime } from '../../utils/dates';
import { eventKindBadgeClass, eventKindLabel, hasSignups } from './eventKind';
import { Avatar } from '../Avatar';
import type { EventAdminDetail } from '../../services/events';
import { EVENT_STATUS_LABEL, EVENT_STATUS_TONE, eventStatus } from './eventStatus';

export type EventDetailTabId = 'details' | 'signups';

interface BreadcrumbCrumb {
    label: string;
    path: string | null;
}

// Same crumbs-array pattern as PracticeFormHeader's Breadcrumb - the caller
// decides how many crumbs there are and which are clickable.
function Breadcrumb({ crumbs }: { crumbs: BreadcrumbCrumb[] }) {
    const navigate = useNavigate();
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            {crumbs.map((crumb, i) => (
                <span key={crumb.label} className="flex items-center gap-1.5 min-w-0">
                    {i > 0 && <ChevronRightIcon className="h-3 w-3 text-gray-300 shrink-0" />}
                    {crumb.path ? (
                        <button
                            type="button"
                            onClick={() => navigate(crumb.path as string)}
                            className="hover:text-gray-800 transition-colors truncate"
                        >
                            {crumb.label}
                        </button>
                    ) : (
                        <span className="font-semibold text-gray-700 truncate">{crumb.label}</span>
                    )}
                </span>
            ))}
        </div>
    );
}

// Reproduces SidebarLayout's default header row (this route hides it via
// `hideHeader` - see App.tsx) with the breadcrumb standing in for the title.
function TopStrip({ crumbs }: { crumbs: BreadcrumbCrumb[] }) {
    return (
        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0">
            <Breadcrumb crumbs={crumbs} />
            <div className="w-10 h-10">
                <Avatar />
            </div>
        </div>
    );
}

function EventMetaCell({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-[0.04em]">
                {label}
            </span>
            <span className="block text-[14px] text-gray-800 truncate mt-1">{children}</span>
        </div>
    );
}

const EVENT_DETAIL_TABS: Array<{ id: EventDetailTabId; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'signups', label: 'Sign ups' }
];

interface TabBarProps {
    activeTab: EventDetailTabId;
    onChange: (tab: EventDetailTabId) => void;
    signupCount: number;
    /** `events.get`. False drops the Sign ups tab entirely - not just disables it. */
    canViewSignups: boolean;
}

// Same visual treatment as PracticeDetailHeader's TabBar: underline on the
// active tab, count pill goes dark when its own tab is active.
function TabBar({ activeTab, onChange, signupCount, canViewSignups }: TabBarProps) {
    const tabs = canViewSignups
        ? EVENT_DETAIL_TABS
        : EVENT_DETAIL_TABS.filter((tab) => tab.id !== 'signups');
    return (
        <div className="px-6 flex gap-6 border-t border-gray-100">
            {tabs.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onChange(tab.id)}
                        className={`pt-3 pb-3 text-sm border-b-2 flex items-center gap-1.5 ${
                            isActive
                                ? 'font-semibold text-gray-900 border-gray-900'
                                : 'font-medium text-gray-500 border-transparent hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                        {tab.id === 'signups' && signupCount > 0 && (
                            <span
                                className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full tabular-nums ${
                                    isActive
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                {signupCount}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

interface EventDetailHeaderProps {
    isCreate: boolean;
    event: EventAdminDetail | null;
    activeTab: EventDetailTabId;
    onTabChange: (tab: EventDetailTabId) => void;
    canViewSignups: boolean;
}

/** Mirrors PracticeDetailHeader's card (name banner + collapsible recap + tab
 *  bar) in edit mode. Create mode is breadcrumb-only - nothing exists yet. */
export function EventDetailHeader({
    isCreate,
    event,
    activeTab,
    onTabChange,
    canViewSignups
}: EventDetailHeaderProps) {
    const [expanded, setExpanded] = useState(true);
    const title = isCreate ? 'New event' : (event?.title ?? '');

    const crumbs: BreadcrumbCrumb[] = [
        { label: 'Accounts', path: '/accounts' },
        { label: 'Events', path: '/accounts/events' },
        { label: title, path: null }
    ];

    if (isCreate || !event) {
        return <TopStrip crumbs={crumbs} />;
    }

    const status = eventStatus(event);

    return (
        <>
            <TopStrip crumbs={crumbs} />
            <div className="px-8 pb-2 mt-6 shrink-0">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)]">
                    <div className="px-6 pt-4 pb-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                            <CalendarDaysIcon className="h-4 w-4" />
                        </div>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <h1
                                className="text-[20px] leading-tight font-bold text-gray-900 truncate min-w-0"
                                title={title}
                            >
                                {title}
                            </h1>
                            <SolidStatusPill
                                value={status}
                                label={EVENT_STATUS_LABEL[status]}
                                toneClassName={EVENT_STATUS_TONE[status]}
                            />
                            <span
                                className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${eventKindBadgeClass(event.kind)}`}
                            >
                                {eventKindLabel(event.kind)}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            title={expanded ? 'Collapse' : 'Expand'}
                            className="shrink-0 w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
                        >
                            <ChevronDownIcon
                                className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                            />
                        </button>
                    </div>
                    {expanded && (
                        <div className="px-6 pb-4 grid grid-cols-4 gap-4">
                            {hasSignups(event) ? (
                                <>
                                    <EventMetaCell label="Topic">{event.topic}</EventMetaCell>
                                    <EventMetaCell label="Starts">
                                        {formatEventWindow(event.startsAt, event.endsAt)}
                                    </EventMetaCell>
                                    <EventMetaCell label="Duration">
                                        {event.durationHours}h
                                    </EventMetaCell>
                                    <EventMetaCell label="Sign-ups">
                                        {event.signupCount}
                                    </EventMetaCell>
                                </>
                            ) : (
                                <>
                                    <EventMetaCell label="Show from">
                                        {formatDateTime(event.showFrom)}
                                    </EventMetaCell>
                                    <EventMetaCell label="Show until">
                                        {formatDateTime(event.showUntil)}
                                    </EventMetaCell>
                                </>
                            )}
                        </div>
                    )}
                    <TabBar
                        activeTab={activeTab}
                        onChange={onTabChange}
                        signupCount={event.signupCount}
                        canViewSignups={canViewSignups}
                    />
                </div>
            </div>
        </>
    );
}
